import { matchPath } from 'react-router-dom';
import type { HelpAreaId } from './types';
import { TUTORIAL_CATALOG, type CatalogEntry } from './catalog';

/**
 * Every route in the site (App.tsx), and the help for it. ONE list: the Help button in the top bar
 * reads it to open the page's article, and Help > Pages draws its table from it.
 *
 * A route with no article yet is still listed, with `articles: []`, so the Help button always has
 * somewhere to go (the help list for the page's area). `catalog` names the video or article IDs
 * planned for the page in tutorials/catalog.md (the plan of record); Help > Pages shows the ones
 * not yet published as Planned (stillPlanned below).
 *
 * src/help/__tests__/pageHelp.test.ts walks App.tsx and fails when a route is missing here, so a new
 * page cannot silently skip help. Add it here, with its article when there is one.
 */
export interface PageHelpEntry {
  /** As App.tsx writes it; a nested route is joined to its parent ("/bedside/pin"). */
  path: string;
  /** The page's name, as the screen or the menu shows it. */
  title: string;
  area: HelpAreaId;
  /** Help article slugs, most useful first. */
  articles: string[];
  /** Planned video or article IDs from tutorials/catalog.md. */
  catalog: string[];
  /**
   * Not a page of its own: `redirect` sends you on to another page; `system` is a sign-in step or
   * a fallback. Listed so the check above stays complete; left out of Help > Pages.
   */
  kind?: 'redirect' | 'system';
  /** Opens on its own screen without the top bar (kitchen display, tablets, bedside devices). */
  standalone?: boolean;
  /**
   * Tabs of the page (its ?tab= value) with help of their own. The Help button on a tab opens the tab's
   * articles; with no ?tab=, the page's own.
   */
  tabs?: PageTabHelp[];
}

export interface PageTabHelp {
  /** The ?tab= value. */
  tab: string;
  /** The tab's name on screen. */
  title: string;
  articles: string[];
  /**
   * Sections and lists inside the tab (no address of their own), for Help > Pages: the Mappings tab's
   * Frequencies section, its Supplements list and the rest.
   */
  parts?: { title: string; articles: string[] }[];
}

type Opts = Partial<Pick<PageHelpEntry, 'articles' | 'catalog' | 'kind' | 'standalone' | 'tabs'>>;

const page = (area: HelpAreaId) => (path: string, title: string, opts: Opts = {}): PageHelpEntry => ({
  path, title, area, articles: opts.articles ?? [], catalog: opts.catalog ?? [], kind: opts.kind, standalone: opts.standalone, tabs: opts.tabs,
});

const redirect: Opts = { kind: 'redirect' };
const system: Opts = { kind: 'system' };

/** Article slugs, so a typo is a type error. */
export const ARTICLE = {
  mapEhrText: 'map-ehr-text',
  ehrSupplementsTubeFeeds: 'ehr-supplements-tube-feeds',
  gettingAround: 'getting-around',
  yourDashboard: 'your-dashboard',
  findingAResident: 'finding-a-resident',
  settingsAndPreferences: 'settings-and-preferences',
  switchingFacility: 'switching-facility',
  runningAReport: 'running-a-report',
  favoritesAndPrintQueue: 'favorites-and-print-queue',
  admitResident: 'admit-resident',
  changeDietOrder: 'change-diet-order',
  allergiesLikesDislikes: 'allergies-likes-dislikes',
  mealPatterns: 'meal-patterns',
  enteringWeights: 'entering-weights',
  dischargeReadmit: 'discharge-readmit',
  selectiveTouch: 'selective-touch',
  trayTickets: 'tray-tickets',
  trayLineSetup: 'tray-line-setup',
  menuCycles: 'menu-cycles',
  readingAMenuCycle: 'reading-a-menu-cycle',
  changingMenuItems: 'changing-menu-items',
  dietAndConsistencyRules: 'diet-and-consistency-rules',
  syncedRowsAndPushTo: 'synced-rows-and-push-to',
  holidayAndOneTimeMenus: 'holiday-and-one-time-menus',
  menuSwapAndRequests: 'menu-swap-and-requests',
  askingForAMenuChange: 'asking-for-a-menu-change',
  askingToSwapTwoMeals: 'asking-to-swap-two-meals',
  autoItemMods: 'auto-item-mods',
  importingAMenu: 'importing-a-menu',
  menuAnalysis: 'menu-analysis',
  usingAnalysis: 'using-analysis',
  ehrConnection: 'ehr-connection',
  firstDay: 'first-day',
  glossary: 'glossary',
  adjustments: 'adjustments',
  printersAndPrinting: 'printers-and-printing',
  nutritionCompliance: 'nutrition-compliance',
  nutritionAssessments: 'nutrition-assessments',
  planOfCare: 'plan-of-care',
  phonesAndTablets: 'phones-and-tablets',
  guests: 'guests',
  itemsAndRecipes: 'items-and-recipes',
  specialEvents: 'special-events',
  rolesAndAccess: 'roles-and-access',
  facilitySetup: 'facility-setup',
  gettingStarted: 'getting-started',
  ehrPage: 'ehr-page',
  ehrFrequenciesActionPlans: 'ehr-frequencies-action-plans',
  ehrHeldDietOrders: 'ehr-held-diet-orders',
  autoTrayLineSetup: 'auto-tray-line-setup',
  allergyProtectionCheck: 'allergy-protection-check',
  printingSetup: 'printing-setup',
  selectiveTouchSetup: 'selective-touch-setup',
  nutritionalAnalysisAndValidation: 'nutritional-analysis-and-validation',
  census: 'census',
  credits: 'credits',
  intakeStudies: 'intake-studies',
  dietConfiguration: 'diet-configuration',
  tables: 'tables',
  bulkOrder: 'bulk-order',
  forecasting: 'forecasting',
  diningRoomsAndEmployees: 'dining-rooms-and-employees',
  workflows: 'workflows',
  surveys: 'surveys',
  menuAndNourishmentCost: 'menu-and-nourishment-cost',
  corporateDashboard: 'corporate-dashboard',
} as const;

const general = page('general');
const home = page('home');
const residents = page('residents');
const clinical = page('clinical');
const menus = page('menus');
const foodData = page('food-data');
const kitchen = page('kitchen');
const purchasing = page('purchasing');
const reports = page('reports');
const setup = page('setup');

export const PAGE_HELP: PageHelpEntry[] = [
  // ─── Getting started and site-wide ───
  general('/', 'Start page', system),
  general('*', 'Page not found', system),
  general('/connection', 'Choose a connection', system),
  general('/help', 'Help', { articles: [ARTICLE.firstDay, ARTICLE.gettingAround, ARTICLE.glossary, ARTICLE.phonesAndTablets], catalog: ['h18', 'h19', 'h33'] }),
  general('/help/:slug', 'Help article'),
  general('/section/:sectionId', 'Section', redirect),
  general('/overview', 'Overview', redirect),
  general('/fhir', 'FHIR', system),
  general('/fhir/terms', 'FHIR terms', system),
  general('/terms', 'Terms', system),
  general('/smart-callback', 'EHR sign-in', system),
  general('/launch', 'EHR launch', system),
  general('/smart-launch', 'EHR launch', system),

  // ─── Home ───
  home('/dashboard', 'Dashboard', {
    articles: [ARTICLE.yourDashboard, ARTICLE.firstDay, ARTICLE.gettingAround, ARTICLE.switchingFacility],
    catalog: ['01', '02', 'q02', 'q03', 'q04', 'h18'],
  }),
  home('/corporate-dashboard', 'Corporate Dashboard', { articles: [ARTICLE.corporateDashboard], catalog: ['h77'] }),

  // ─── Residents ───
  residents('/people', 'Residents', {
    articles: [ARTICLE.findingAResident, ARTICLE.admitResident, ARTICLE.dischargeReadmit, ARTICLE.trayTickets],
    catalog: ['03', '05', '10', 'q01'],
  }),
  residents('/people/classic', 'Residents (old address)', redirect),
  residents('/people/deleted', 'Manage Residents', { articles: [ARTICLE.census] }),
  residents('/people/new', 'New resident', { articles: [ARTICLE.admitResident], catalog: ['05'] }),
  residents('/people/:id', 'Resident', {
    articles: [
      ARTICLE.changeDietOrder, ARTICLE.ehrHeldDietOrders, ARTICLE.allergiesLikesDislikes, ARTICLE.mealPatterns, ARTICLE.admitResident,
      ARTICLE.trayTickets, ARTICLE.adjustments, ARTICLE.ehrSupplementsTubeFeeds, ARTICLE.ehrConnection,
    ],
    catalog: ['06', '07', '08', '09', '10', 'q05', 'h29', 'h03'],
    // Sections of pages/people/ResidentDetail.tsx (its ?tab= keys) with help of their own.
    tabs: [
      { tab: 'diet-info', title: 'Diet Order', articles: [ARTICLE.changeDietOrder] },
      { tab: 'diet-flucs', title: 'Diet Fluctuations', articles: [ARTICLE.changeDietOrder, ARTICLE.ehrHeldDietOrders, ARTICLE.ehrConnection] },
      { tab: 'allergies', title: 'Food Allergies', articles: [ARTICLE.allergiesLikesDislikes, ARTICLE.admitResident] },
      { tab: 'adjustments', title: 'Adjustments', articles: [ARTICLE.adjustments, ARTICLE.allergiesLikesDislikes] },
      { tab: 'meal-pattern', title: 'Meal Pattern', articles: [ARTICLE.mealPatterns, ARTICLE.ehrSupplementsTubeFeeds] },
      { tab: 'tray-line', title: 'Tray Line', articles: [ARTICLE.admitResident, ARTICLE.trayLineSetup] },
      { tab: 'weight-history', title: 'Weight History', articles: [ARTICLE.enteringWeights] },
      { tab: 'tube-feed', title: 'Tube Feed', articles: [ARTICLE.ehrSupplementsTubeFeeds] },
      { tab: 'assessments', title: 'Assessments', articles: [ARTICLE.nutritionAssessments] },
      { tab: 'plan-of-care', title: 'Plan of Care', articles: [ARTICLE.planOfCare] },
    ],
  }),
  residents('/people/guests', 'Guests', { articles: [ARTICLE.guests], catalog: ['h34'] }),
  residents('/people/guests/profile/new', 'New guest', { articles: [ARTICLE.guests], catalog: ['h34'] }),
  residents('/people/guests/profile/:id', 'Guest', { articles: [ARTICLE.guests], catalog: ['h34'] }),
  residents('/manage-residents', 'Manage Residents', redirect),
  residents('/emr', 'EHR', {
    articles: [ARTICLE.ehrPage, ARTICLE.ehrConnection, ARTICLE.mapEhrText, ARTICLE.ehrSupplementsTubeFeeds, ARTICLE.ehrFrequenciesActionPlans],
    // The tabs of pages/emr/EmrPage.tsx. The pop-up and badges on Pending Messages are in Map EHR text.
    tabs: [
      { tab: 'pending', title: 'Pending Messages', articles: [ARTICLE.ehrPage, ARTICLE.mapEhrText] },
      { tab: 'history', title: 'Message History', articles: [ARTICLE.ehrPage] },
      {
        tab: 'mappings', title: 'Mappings', articles: [ARTICLE.mapEhrText, ARTICLE.ehrPage, ARTICLE.ehrSupplementsTubeFeeds],
        // Its sections (EhrMappingsPanel SECTIONS), then the kinds of mapping listed on Mappings (mappingRules).
        parts: [
          { title: 'Mappings section', articles: [ARTICLE.mapEhrText] },
          { title: 'Frequencies section', articles: [ARTICLE.ehrFrequenciesActionPlans, ARTICLE.ehrSupplementsTubeFeeds] },
          { title: 'Exclusions section', articles: [ARTICLE.ehrPage] },
          { title: 'Unit Mappings section', articles: [ARTICLE.ehrPage] },
          { title: 'Room/Bed Mappings section', articles: [ARTICLE.ehrPage] },
          { title: 'Mapping health section', articles: [ARTICLE.mapEhrText] },
          { title: 'Supplements list', articles: [ARTICLE.ehrSupplementsTubeFeeds] },
          { title: 'Tube Feeds list', articles: [ARTICLE.ehrSupplementsTubeFeeds] },
          { title: 'Action Plans list', articles: [ARTICLE.ehrFrequenciesActionPlans] },
        ],
      },
      { tab: 'allergies', title: 'Allergy Changes', articles: [ARTICLE.mapEhrText, ARTICLE.ehrPage] },
      { tab: 'added', title: 'Added Items', articles: [ARTICLE.ehrSupplementsTubeFeeds] },
    ],
  }),
  residents('/weight-entry', 'Weight Entry', { articles: [ARTICLE.enteringWeights], catalog: ['09'] }),
  residents('/intake-studies', 'Intake Studies', { articles: [ARTICLE.intakeStudies], catalog: ['h38'] }),
  residents('/census', 'Census', { articles: [ARTICLE.census], catalog: ['h36'] }),
  residents('/credit-process', 'Credits', { articles: [ARTICLE.credits], catalog: ['h37'] }),

  // ─── Clinical ───
  clinical('/clinical-nutrition', 'Clinical', redirect),
  clinical('/nutritional', 'Clinical', redirect),
  clinical('/clinical', 'Clinical', redirect),
  clinical('/assessments', 'Assessments', { articles: [ARTICLE.nutritionAssessments], catalog: ['21', 'h31'] }),
  clinical('/assessments/:id', 'Assessment', { articles: [ARTICLE.nutritionAssessments], catalog: ['21', 'h31'] }),
  clinical('/assessments/new/:patientId', 'New assessment', { articles: [ARTICLE.nutritionAssessments], catalog: ['21', 'h31'] }),
  clinical('/assessments/:id/edit', 'Edit assessment', { articles: [ARTICLE.nutritionAssessments], catalog: ['21', 'h31'] }),
  clinical('/plan-of-care', 'Plan of Care', { articles: [ARTICLE.planOfCare], catalog: ['22', 'h32'] }),
  clinical('/plan-of-care/configuration', 'Plan of Care Setup', { articles: [ARTICLE.planOfCare], catalog: ['h32'] }),
  clinical('/plan-of-care/evaluation/:patientId/:headerId', 'Plan of Care evaluation', { articles: [ARTICLE.planOfCare], catalog: ['22', 'h32'] }),
  clinical('/plan-of-care/:id', 'Plan of Care', redirect),
  clinical('/pdpm', 'Nutrition Compliance', { articles: [ARTICLE.nutritionCompliance], catalog: ['h30'] }),
  clinical('/nutritional-validation', 'Validation', { articles: [ARTICLE.nutritionalAnalysisAndValidation], catalog: ['h35'] }),
  clinical('/nutritional-analysis', 'Nutritional Analysis', { articles: [ARTICLE.nutritionalAnalysisAndValidation], catalog: ['18', 'h35'] }),
  clinical('/analysis/nutritional', 'Nutritional Analysis', { articles: [ARTICLE.nutritionalAnalysisAndValidation], catalog: ['18', 'h35'] }),

  // ─── Menus ───
  menus('/menucycle-hub', 'Menus', redirect),
  menus('/menucycle', 'Menu Cycles', {
    catalog: ['16', 'h40'],
    // Importing a menu is New Menu's, PSI staff only (Jeff, 2026-11-04).
    articles: [ARTICLE.menuCycles, ARTICLE.readingAMenuCycle, ARTICLE.holidayAndOneTimeMenus],
  }),
  menus('/menucycle/today', "Today's Menu", { articles: [ARTICLE.readingAMenuCycle, ARTICLE.holidayAndOneTimeMenus] }),
  menus('/menucycle/workbench/:menuHeaderId', 'Menu Cycle Workbench', {
    catalog: ['16', 'h41', 'h42', 'h43', 'h44', 'h45'],
    articles: [
      ARTICLE.readingAMenuCycle, ARTICLE.changingMenuItems, ARTICLE.dietAndConsistencyRules,
      ARTICLE.syncedRowsAndPushTo, ARTICLE.holidayAndOneTimeMenus, ARTICLE.menuAnalysis,
    ],
  }),
  menus('/menucycle/basic/:menuHeaderId', 'Menu Cycle', { articles: [ARTICLE.menuCycles, ARTICLE.readingAMenuCycle], catalog: ['16'] }),
  menus('/menucycle/beta/:menuHeaderId', 'Menu Cycle', redirect),
  // New Menu (PSI staff only) and Menu Changes hold what were Menu Import, Menu Builder, Change Requests and
  // Request a Menu Change (navigation/menuPages.ts); their old addresses redirect to the tab.
  menus('/menucycle/new', 'New Menu', {
    catalog: ['h48'],
    articles: [ARTICLE.importingAMenu],
    tabs: [
      { tab: 'import', title: "Import a vendor's menu", articles: [ARTICLE.importingAMenu] },
      { tab: 'draft', title: 'Draft with AI', articles: [] },
      { tab: 'memory', title: 'What the AI remembers', articles: [] },
    ],
  }),
  menus('/menucycle/new/import/:draftId/:stage?', 'New Menu', { catalog: ['h48'], articles: [ARTICLE.importingAMenu] }),
  menus('/menucycle/changes', 'Menu Changes', {
    catalog: ['h50', 'h51', 'h46'],
    articles: [ARTICLE.askingForAMenuChange, ARTICLE.askingToSwapTwoMeals, ARTICLE.menuSwapAndRequests],
    tabs: [
      { tab: 'ask', title: 'Ask PSI', articles: [ARTICLE.askingForAMenuChange, ARTICLE.askingToSwapTwoMeals] },
      { tab: 'sheets', title: 'From extension sheets', articles: [ARTICLE.menuSwapAndRequests] },
      { tab: 'ai', title: 'Change with AI', articles: [] },
    ],
  }),
  menus('/menucycle/import', 'New Menu', redirect),
  menus('/menucycle/builder', 'New Menu', redirect),
  menus('/menucycle/import/:draftId/:stage?', 'New Menu', redirect),
  menus('/menucycle/analysis/:menuHeaderId', 'Menu Analysis', { catalog: ['18', 'h49'], articles: [ARTICLE.menuAnalysis] }),
  menus('/menucycle/swap/:menuHeaderId', 'Menu Swap', { catalog: ['h46'], articles: [ARTICLE.menuSwapAndRequests] }),
  menus('/menucycle/requests', 'Menu Changes', redirect),
  menus('/menucycle/request-change', 'Menu Changes', redirect),
  menus('/menucycle/changerequest', 'Menu Changes', redirect),
  menus('/menucycle/recyclebin', 'Deleted menus', { catalog: ['h40'], articles: [ARTICLE.menuCycles] }),
  menus('/menucycle/aim', 'Auto Item Mods', { catalog: ['h47'], articles: [ARTICLE.autoItemMods] }),
  menus('/menucycle/excel-diet-orders', 'Excel Diet Orders'),
  menus('/special-events', 'Special Events', { articles: [ARTICLE.specialEvents], catalog: ['14', 'h53'] }),
  menus('/special-events/:id', 'Special event', { articles: [ARTICLE.specialEvents], catalog: ['14', 'h53'] }),
  menus('/global-adjustments', 'Global Adjustments', { articles: [ARTICLE.adjustments], catalog: ['h29'] }),
  menus('/global-adjustments/:id', 'Global adjustment', { articles: [ARTICLE.adjustments], catalog: ['h29'] }),
  menus('/meal-patterns', 'Meal Pattern Groups', { articles: [ARTICLE.mealPatterns], catalog: ['08'] }),
  menus('/global-meal-patterns', 'Global Meal Patterns', { articles: [ARTICLE.mealPatterns], catalog: ['08'] }),
  menus('/global-meal-patterns/:id', 'Global meal pattern', { articles: [ARTICLE.mealPatterns], catalog: ['08'] }),

  // ─── Food Data ───
  foodData('/food-data', 'Food Data', redirect),
  foodData('/tables/:tableKey?', 'Tables', { articles: [ARTICLE.tables], catalog: ['h55'] }),
  foodData('/items', 'Items', { articles: [ARTICLE.itemsAndRecipes], catalog: ['17', 'h52'] }),
  foodData('/items/new', 'New item', { articles: [ARTICLE.itemsAndRecipes], catalog: ['17', 'h52'] }),
  foodData('/items/:id', 'Item', { catalog: ['17', 'h52'], articles: [ARTICLE.itemsAndRecipes, ARTICLE.autoItemMods] }),
  foodData('/items/:id/edit', 'Item', { catalog: ['17', 'h52'], articles: [ARTICLE.itemsAndRecipes, ARTICLE.autoItemMods] }),
  foodData('/items/usda-matcher', 'USDA Matcher'),
  foodData('/items/linking', 'Item Linking'),
  foodData('/items/order-guide-assignment', 'Order Guide/Shelf Assignment'),
  foodData('/recipes', 'Recipes', { articles: [ARTICLE.itemsAndRecipes], catalog: ['17', 'h52'] }),
  foodData('/recipes/new', 'New recipe', { articles: [ARTICLE.itemsAndRecipes], catalog: ['17', 'h52'] }),
  foodData('/recipes/:id', 'Recipe', { articles: [ARTICLE.itemsAndRecipes], catalog: ['17', 'h52'] }),
  foodData('/recipes/:id/edit', 'Recipe', { articles: [ARTICLE.itemsAndRecipes], catalog: ['17', 'h52'] }),
  foodData('/diet-config', 'Diet Configuration', { articles: [ARTICLE.dietConfiguration], catalog: ['h54'] }),

  // ─── Kitchen ───
  kitchen('/kitchen-home', 'Kitchen'),
  kitchen('/operations', 'Kitchen', redirect),
  kitchen('/selective-touch/*', 'Selective Touch', { articles: [ARTICLE.selectiveTouch], catalog: ['11'] }),
  kitchen('/tray-line-setup', 'Tray Line Setup', { articles: [ARTICLE.trayLineSetup, ARTICLE.autoTrayLineSetup, ARTICLE.trayTickets], catalog: ['13', 'h64'] }),
  kitchen('/bulk-order', 'Bulk Order', { articles: [ARTICLE.bulkOrder], catalog: ['h71'] }),
  kitchen('/forecasting', 'Forecasting', { articles: [ARTICLE.forecasting], catalog: ['h72'] }),
  kitchen('/dining-rooms', 'Dining Rooms', { articles: [ARTICLE.diningRoomsAndEmployees], catalog: ['h73'] }),
  kitchen('/employees', 'Employees', { articles: [ARTICLE.diningRoomsAndEmployees], catalog: ['h73'] }),
  kitchen('/workflows/*', 'Workflows', { articles: [ARTICLE.workflows], catalog: ['h74'] }),
  kitchen('/kitchen', 'Kitchen Display', { standalone: true }),
  kitchen('/self-order', 'Self-Order', { standalone: true }),
  kitchen('/tablet/*', 'Tablet', { standalone: true }),
  kitchen('/bedside', 'Bedside', { standalone: true }),
  kitchen('/bedside/activate/:deviceToken', 'Bedside activation', { standalone: true }),
  kitchen('/bedside/pin', 'Bedside PIN', { standalone: true }),
  kitchen('/bedside/welcome', 'Bedside welcome', { standalone: true }),
  kitchen('/bedside/menu', 'Bedside menu', { standalone: true }),
  kitchen('/bedside/select', 'Bedside choices', { standalone: true }),
  kitchen('/bedside/confirm', 'Bedside confirmation', { standalone: true }),
  kitchen('/bedside/status', 'Bedside order status', { standalone: true }),
  kitchen('/bedside/tracking', 'Bedside tracking', { standalone: true }),
  kitchen('/bedside/rate', 'Bedside rating', { standalone: true }),
  kitchen('/bedside/diag', 'Bedside diagnostics', { standalone: true }),
  kitchen('/bedside/snack', 'Bedside snacks', { standalone: true }),
  kitchen('/bedside/guest-tray', 'Bedside guest tray', { standalone: true }),

  // ─── Purchasing ───
  purchasing('/ordering', 'Purchasing'),
  purchasing('/purchasing', 'Purchasing', redirect),
  purchasing('/purchasing/*', 'Purchasing', redirect),
  purchasing('/ordering/orders', 'Purchase Orders'),
  purchasing('/ordering/orders/new', 'New purchase order'),
  purchasing('/ordering/orders/:id', 'Purchase order'),
  purchasing('/ordering/order-guides', 'Order Guides'),
  purchasing('/ordering/reorder', 'Reorder'),
  purchasing('/ordering/requisitions', 'Requisitions'),
  purchasing('/ordering/requisitions/new', 'New requisition'),
  purchasing('/ordering/requisitions/:id', 'Requisition'),
  purchasing('/ordering/demand', 'Demand Planning'),
  purchasing('/ordering/receiving', 'Receiving'),
  purchasing('/ordering/receiving/:poId', 'Receiving'),
  purchasing('/ordering/invoices', 'Vendor Invoices'),
  purchasing('/ordering/invoices/new', 'New vendor invoice'),
  purchasing('/ordering/invoices/:id', 'Vendor invoice'),
  purchasing('/ordering/credit-memos', 'Credit Memos'),
  purchasing('/ordering/credit-memos/new', 'New credit memo'),
  purchasing('/ordering/credit-memos/:id', 'Credit memo'),
  purchasing('/ordering/inventory', 'Inventory'),
  purchasing('/ordering/inventory/items', 'Inventory items'),
  purchasing('/ordering/inventory/items/new', 'New inventory item'),
  purchasing('/ordering/inventory/items/:id', 'Inventory item'),
  purchasing('/ordering/inventory/transactions', 'Inventory transactions'),
  purchasing('/ordering/inventory/counts', 'Inventory counts'),
  purchasing('/ordering/inventory/counts/:id', 'Inventory count'),
  purchasing('/ordering/inventory/floor-stock', 'Floor stock'),
  purchasing('/ordering/inventory-planning', 'Inventory Planning'),
  purchasing('/ordering/waste', 'Waste'),
  purchasing('/ordering/meal-cost', 'Meal Cost'),
  purchasing('/ordering/suppliers', 'Supplier Accounts'),
  purchasing('/ordering/suppliers/new', 'New supplier'),
  purchasing('/ordering/suppliers/:id', 'Supplier'),
  purchasing('/ordering/item-links', 'Item Links'),
  purchasing('/ordering/catalog', 'Price Updates'),
  purchasing('/ordering/contracts', 'Contracts'),
  purchasing('/ordering/contracts/new', 'New contract'),
  purchasing('/ordering/contracts/:id', 'Contract'),
  purchasing('/ordering/pricing-import', 'Price Updates'),
  purchasing('/ordering/contacts', 'Contacts'),
  purchasing('/ordering/edi', 'EDI'),
  purchasing('/ordering/costing', 'Costing'),
  purchasing('/ordering/prep-schedule', 'Prep Schedule'),
  purchasing('/ordering/allergens', 'Allergens'),
  purchasing('/ordering/spend', 'Spend'),
  purchasing('/ordering/scorecard', 'Supplier Scorecard'),
  purchasing('/ordering/corporate', 'Corporate Purchasing'),
  purchasing('/ordering/setup', 'Purchasing Setup'),
  purchasing('/purchasing/departments', 'Departments'),
  purchasing('/purchasing/ledgers', 'Ledgers'),

  // ─── Reports ───
  reports('/reports/*', 'Reports', {
    articles: [ARTICLE.runningAReport, ARTICLE.favoritesAndPrintQueue, ARTICLE.trayTickets, ARTICLE.printersAndPrinting],
    catalog: ['19', '20', '12', 'h60'],
  }),
  reports('/surveys', 'Surveys', { articles: [ARTICLE.surveys], catalog: ['h75'] }),
  reports('/surveys/templates/new', 'New survey template', { articles: [ARTICLE.surveys], catalog: ['h75'] }),
  reports('/surveys/templates/:id', 'Survey template', { articles: [ARTICLE.surveys], catalog: ['h75'] }),
  reports('/surveys/sessions', 'Survey sessions', { articles: [ARTICLE.surveys], catalog: ['h75'] }),
  reports('/surveys/sessions/new', 'New survey session', { articles: [ARTICLE.surveys], catalog: ['h75'] }),
  reports('/surveys/sessions/:id', 'Survey session', { articles: [ARTICLE.surveys], catalog: ['h75'] }),
  reports('/surveys/sessions/:id/collect', 'Collect survey responses', { articles: [ARTICLE.surveys], catalog: ['h75'] }),
  reports('/surveys/analysis', 'Survey analysis', { articles: [ARTICLE.surveys], catalog: ['h75'] }),
  reports('/analysis', 'Analysis', { ...redirect, articles: [ARTICLE.usingAnalysis] }),
  // The seven Analysis screens are topics of one page (/analysis/diet-orders ... /analysis/employee-served-items).
  reports('/analysis/:topic', 'Analysis', { articles: [ARTICLE.usingAnalysis] }),
  reports('/analysis/cost-menu', 'Menu Cost', { articles: [ARTICLE.menuAndNourishmentCost], catalog: ['h76'] }),
  reports('/analysis/cost-nourishment', 'Nourishment Cost', { articles: [ARTICLE.menuAndNourishmentCost], catalog: ['h76'] }),
  reports('/analysis/demand-forecasting', 'Demand Forecasting'),

  // ─── Setup ───
  setup('/settings', 'Setup', { articles: [ARTICLE.gettingStarted, ARTICLE.facilitySetup, ARTICLE.rolesAndAccess], catalog: ['15'] }),
  setup('/application-settings', 'Setup', redirect),
  setup('/facilities', 'Facilities', { articles: [ARTICLE.facilitySetup], catalog: ['24', 'h62'] }),
  setup('/facilities/:id', 'Facility Settings', { articles: [ARTICLE.facilitySetup, ARTICLE.printersAndPrinting], catalog: ['24', 'h62', 'h60'] }),
  setup('/facilities/:id/units', 'Units', { articles: [ARTICLE.facilitySetup], catalog: ['24', 'h62'] }),
  setup('/unit-rooms', 'Unit Rooms', { articles: [ARTICLE.facilitySetup], catalog: ['24', 'h62'] }),
  setup('/printers', 'Printers', { articles: [ARTICLE.printersAndPrinting], catalog: ['25', 'h60'] }),
  setup('/print-schedules', 'Print Schedules', { articles: [ARTICLE.printersAndPrinting], catalog: ['25', 'h60'] }),
  setup('/users', 'Users', { articles: [ARTICLE.rolesAndAccess], catalog: ['23', 'h61'] }),
  setup('/users/new', 'New user', { articles: [ARTICLE.rolesAndAccess], catalog: ['23'] }),
  setup('/users/deleted', 'Deleted Users'),
  setup('/users/logged-in', 'Logged In Users'),
  setup('/users/:id', 'User', { articles: [ARTICLE.rolesAndAccess], catalog: ['23'] }),
  setup('/roles', 'Roles', { articles: [ARTICLE.rolesAndAccess], catalog: ['23', 'h61'] }),
  setup('/roles/new', 'New role', { articles: [ARTICLE.rolesAndAccess], catalog: ['23', 'h61'] }),
  setup('/roles/:id', 'Role', { articles: [ARTICLE.rolesAndAccess], catalog: ['23', 'h61'] }),
  setup('/roles/:id/edit', 'Role', { articles: [ARTICLE.rolesAndAccess], catalog: ['23', 'h61'] }),
  setup('/roles/:id/usage', 'Role usage', { articles: [ARTICLE.rolesAndAccess], catalog: ['h61'] }),
  setup('/getting-started/:step?', 'Getting Started', { articles: [ARTICLE.gettingStarted, ARTICLE.autoTrayLineSetup, ARTICLE.allergyProtectionCheck, ARTICLE.printingSetup, ARTICLE.selectiveTouchSetup], catalog: ['h63', 'h64', 'h65', 'h66', 'h67'] }),
  setup('/allergy-protection', 'Check allergy protection', { articles: [ARTICLE.allergyProtectionCheck, ARTICLE.allergiesLikesDislikes], catalog: ['h65'] }),
  setup('/settings/dietech-data-update', 'Data Update', { articles: [ARTICLE.gettingStarted], catalog: ['h63'] }),
  setup('/system-log', 'System Log'),
  setup('/admin/status', 'System Status'),
  setup('/admin/feedback', 'Test Kitchen'),
  setup('/user-settings', 'My Settings', {
    articles: [ARTICLE.settingsAndPreferences, ARTICLE.switchingFacility],
    catalog: ['04'],
  }),
];

/**
 * The entry for an address. Exact paths win over patterns, and longer patterns over shorter, the way
 * react-router ranks routes ("/people/new" is not "/people/:id"). The catch-all "*" never matches here.
 */
export function findPageHelp(pathname: string): PageHelpEntry | undefined {
  const ranked = PAGE_HELP
    .filter((e) => e.path !== '*')
    .map((e) => ({ e, score: routeScore(e.path) }))
    .sort((a, b) => b.score - a.score);
  return ranked.find(({ e }) => matchPath({ path: e.path, end: true }, pathname))?.e;
}

/** Static segments count most, then params, then splats: enough to order this list. */
function routeScore(path: string): number {
  return path.split('/').filter(Boolean).reduce((n, seg) => n + (seg === '*' ? 1 : seg.startsWith(':') ? (seg.endsWith('?') ? 2 : 3) : 10), 0);
}

/** The help for an address and its ?tab=: the tab's articles when the page lists that tab, else the page's. */
export function articlesFor(pathname: string, search = ''): { entry: PageHelpEntry | undefined; tab?: PageTabHelp; articles: string[] } {
  const entry = findPageHelp(pathname);
  const tabKey = new URLSearchParams(search).get('tab');
  const tab = tabKey ? entry?.tabs?.find((t) => t.tab === tabKey) : undefined;
  return { entry, tab, articles: tab ? tab.articles : entry?.articles ?? [] };
}

/** The real pages (no redirects or sign-in steps), for Help > Pages. */
export function helpPages(): PageHelpEntry[] {
  return PAGE_HELP.filter((e) => !e.kind);
}

/** The catalog entries for these IDs that are not published yet: what Help > Pages calls Planned. */
export function stillPlanned(ids: string[]): CatalogEntry[] {
  return ids
    .map((id) => TUTORIAL_CATALOG.find((c) => c.id === id))
    .filter((c): c is CatalogEntry => !!c && c.status !== 'published');
}
